import React, { useEffect, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import ModalPreview from './modals/ModalPreview';
import { useTranslation } from 'react-i18next';
import { ClickAwayListener, Pagination, PaginationItem, Tooltip } from '@mui/material';
import ShowFileIcon from '../../assets/iconsSmall/list.svg';
import PandaMain from '../../assets/UserInfo/panda-logo-main.svg';
import SortAsc from '../../assets/iconsSmall/sortAsc.svg';
import ArrowLeft from '../../assets/PetPage/arrow_left_dashed_small.svg';
import ArrowRight from '../../assets/PetPage/arrow_right_dashed.svg';
import Question from '../../assets/iconsSmall/red_question.svg';
import { ReactComponent as ArrowLeftRed } from '../../assets/iconsSmall/arrow_left_solid_red.svg';
import { ReactComponent as ArrowRightRed } from '../../assets/iconsSmall/arrow_right_solid_red.svg';
import {
  claimsTableHeaders,
  claimsTableHeadersAdmin,
  formatAnyDate,
  formatTableDate,
} from '../../config/helpers';
import { isMobile } from 'react-device-detect';
import { USER_MODE } from '../../constants/ApplicationConstants';
import { useTableScroll } from '../../hooks/useTableScroll';

function PolicyTable({
  policyData,
  mode,
}) {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentRow, setCurrentRow] = useState({});
  const [sortedColumn, setSortedColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [sortedData, setSortedData] = useState(policyData);
  const [currentClaim, setCurrentClaim] = useState(1);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const handlePage = (event, value) => {
    setCurrentClaim(value);
  };

  const decreasePageNumber = () => {
    if (currentClaim > 1) {
      setCurrentClaim(currentClaim - 1);
    }
  };

  const increasePageNumber = () => {
    if (currentClaim < sortedData.length) {
      setCurrentClaim(currentClaim + 1);
    }
  };

  const handleSort = (column) => {
    const isAsc = sortedColumn === column && sortDirection === 'asc';
    setSortDirection(isAsc ? 'desc' : 'asc');
    setSortedColumn(column);
    const sorted = [...policyData].sort((a, b) => {
      let valueA, valueB;

      if (column === 'invoiceDate') {
        valueA = formatTableDate(a?.invoices?.[0]?.invoiceDate || a?.lossDate);
        valueB = formatTableDate(b?.invoices?.[0]?.invoiceDate || b?.lossDate);
      } else if (column === 'claimNo') {
        valueA = a.claimNo || t('noInfo');
        valueB = b.claimNo || t('noInfo');
      } else if (column === 'diagnosis') {
        valueA = a.mainDiagnosis || t('noInfo');
        valueB = b.mainDiagnosis || t('noInfo');
      } else if (column === 'petName') {
        valueA = a.petName || t('noInfo');
        valueB = b.petName || t('noInfo');
      } else if (column === 'Status') {
        valueA = a.status?.toLowerCase() || '';
        valueB = b.status?.toLowerCase() || '';
      } else if (column === 'veterinarian') {
        valueA = a.invoices?.[0]?.veterinary || t('noInfo');
        valueB = b.invoices?.[0]?.veterinary || t('noInfo');
      }

      if (column === 'invoiceDate') {
        // Compare dates if both are valid dates
        if (valueA && valueB) {
          valueA = new Date(valueA);
          valueB = new Date(valueB);
          if (valueA < valueB) return isAsc ? 1 : -1;
          if (valueA > valueB) return isAsc ? -1 : 1;
          return 0;
        }
        // Handle cases where one or both dates are invalid
        if (!valueA) return isAsc ? 1 : -1;
        if (!valueB) return isAsc ? -1 : 1;
      } else {
        // Handle other columns as strings
        if (valueA < valueB) return isAsc ? 1 : -1;
        if (valueA > valueB) return isAsc ? -1 : 1;
        return 0;
      }
    });

    setSortedData(sorted);
  };

  useEffect(() => {
    setSortedData([...policyData]);
    handleSort('invoiceDate');
    recalculate();
  }, [policyData]);

  const { bodyRef, headerRef, syncScroll, listOfItemsRef, recalculate } =
    useTableScroll();

  const getStatusInfo = (claimStatus) => {
    if (!claimStatus) {
      return { display: t('noInfo'), tooltip: null, hasMapping: false };
    }
    const statusMap = t('claimStatusMap', { returnObjects: true });
    const statusInfo = statusMap?.[claimStatus];
    if (statusInfo) {
      return { display: statusInfo.display, tooltip: statusInfo.tooltip, hasMapping: true };
    }
    return { display: claimStatus, tooltip: null, hasMapping: false };
  };

  return (
    <>
      {!isMobile && (
        <img
          src={PandaMain}
          alt="Panda_Main_Logo"
          className="main-logo-policy"
        />
      )}
      {isModalOpen && (
        <ModalPreview
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          row={currentRow}
        />
      )}

      {/* TABLE */}

      {isMobile ? (
        <div className="SlowShow policy-table-mobile">
          <div className="policy-table-mobile__header">
            <div
              className="policy-table-mobile__back"
              style={currentClaim == 1 ? { opacity: '0.5' } : {}}
            >
              <div
                className="button_red_mobile back margin-0"
                onClick={decreasePageNumber}
              >
                <img src={ArrowLeft} alt="Back" />
                {t('back')}
              </div>
            </div>
            <div className="policy-table-mobile__img">
              <img
                src={PandaMain}
                alt="Panda_Main_Logo"
                className="main-logo-policy__mobile"
              />
            </div>
            <div
              className="policy-table-mobile__next"
              style={
                currentClaim == sortedData.length ? { opacity: '0.5' } : {}
              }
            >
              <div
                className="button_red_mobile back margin-0"
                onClick={increasePageNumber}
              >
                {t('next')}
                <img src={ArrowRight} alt="Next" />
              </div>
            </div>
          </div>
          <div className="policy-table-mobile__row">
            <div className="policy-table-mobile__left">{t('Date')}</div>
            <div className="policy-table-mobile__right">
              {formatAnyDate(
                sortedData[currentClaim - 1]?.invoices?.[0]?.invoiceDate ||
                  sortedData[currentClaim - 1]?.lossDate
              ) || t('noInfo')}
            </div>
          </div>
          <div className="policy-table-mobile__row">
            <div className="policy-table-mobile__left">{t('claimNo')}</div>
            <div className="policy-table-mobile__right">
              {sortedData[currentClaim - 1]?.claimNo || t('noInfo')}
            </div>
          </div>
          {mode === USER_MODE.ADMIN && (
            <div className="policy-table-mobile__row">
              <div className="policy-table-mobile__left">{t('petName')}</div>
              <div className="policy-table-mobile__right">
                {sortedData[currentClaim - 1]?.petName ||
                  t('noInfo')}
              </div>
            </div>
          )}
          <div className="policy-table-mobile__row">
            <div className="policy-table-mobile__left">{t('diagnosis')}</div>
            <div className="policy-table-mobile__right">
              {sortedData[currentClaim - 1]?.mainDiagnosis || t('noInfo')}
            </div>
          </div>
          <div className="policy-table-mobile__row">
            <div className="policy-table-mobile__left">{t('veterinarian')}</div>
            <div className="policy-table-mobile__right">
              {sortedData[currentClaim - 1]?.invoices?.[0]?.veterinary ||
                t('noInfo')}
            </div>
          </div>
          <div className="policy-table-mobile__row">
            <div className="policy-table-mobile__left">{t('Status')}</div>
            <div className="policy-table-mobile__right">
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
                <span>
                  {getStatusInfo(sortedData[currentClaim - 1]?.status).display}
                </span>
                {getStatusInfo(sortedData[currentClaim - 1]?.status).hasMapping && (
                  <ClickAwayListener onClickAway={() => setTooltipOpen(false)}>
                    <Tooltip
                      title={getStatusInfo(sortedData[currentClaim - 1]?.status).tooltip}
                      placement="top"
                      open={tooltipOpen}
                      disableFocusListener
                      disableHoverListener
                      disableTouchListener
                    >
                      <img 
                        src={Question} 
                        alt="info" 
                        onClick={() => setTooltipOpen(!tooltipOpen)}
                        style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                      />
                    </Tooltip>
                  </ClickAwayListener>
                )}
              </div>
            </div>
          </div>
          <div className="policy-table-mobile__row">
            <div className="policy-table-mobile__left">{t('Details')}</div>
            <div
              className="policy-table-mobile__right"
              style={{ padding: '6px 0' }}
            >
              <div className="d-flex justify-content-evenly align-items-center">
                {sortedData[currentClaim - 1]?.documents &&
                sortedData[currentClaim - 1]?.documents.length > 0 ? (
                  <Tooltip title={t('seeInitDoc')} placement="top">
                    <div
                      className="back_icon_red mobile"
                      onClick={() => {
                        setCurrentRow(sortedData[currentClaim - 1]);
                        setIsModalOpen(true);
                      }}
                    >
                      <img src={ShowFileIcon} />
                    </div>
                  </Tooltip>
                ) : (
                  <Tooltip title={t('seeInitDoc')} placement="top">
                    <div
                      className="back_icon_red mobile"
                      onClick={(e) => e.stopPropagation()}
                      style={{ opacity: '50%' }}
                    >
                      <img src={ShowFileIcon} />
                    </div>
                  </Tooltip>
                )}
              </div>
            </div>
          </div>

          {sortedData.length > 1 && (
            <div className="policy-table-mobile__pagination">
              <Pagination
                className="reset-pagination"
                count={sortedData.length}
                page={currentClaim}
                siblingCount={1}
                boundaryCount={1}
                onChange={handlePage}
                renderItem={(item) => (
                  <PaginationItem
                    slots={{ previous: ArrowLeftRed, next: ArrowRightRed }}
                    {...item}
                    // sx={{
                    //   color: '#FFF',
                    //   backgroundColor: item.type === 'page' && item.page === currentClaim ? '#FE4B4E' : '#DF9F9B',
                    // }}
                  />
                )}
              />
            </div>
          )}
        </div>
      ) : (
        <div>
          <TableContainer
            className="reset-table-spacing table-reset-header"
            sx={{ margin: '0 0 -3px 0' }}
            ref={headerRef}
          >
            <Table
              className={`SlowShow reset-table-new ${
                mode === USER_MODE.ADMIN ? 'policy-table-admin' : 'policy-table'
              }`}
            >
              <TableHead>
                <TableRow className="reset-table-new__header">
                  {(mode === USER_MODE.ADMIN
                    ? claimsTableHeadersAdmin
                    : claimsTableHeaders
                  ).map((header, index) => (
                    <TableCell
                      key={`header-${index}`}
                      align={index > 0 ? 'inherit' : 'left'}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          justifyContent: 'center',
                        }}
                      >
                        <div>
                          <span>{t(header.title)}</span>
                          {header.sort && mode === USER_MODE.ADMIN && (
                            <img
                              alt=""
                              style={{ cursor: 'pointer' }}
                              src={SortAsc}
                              onClick={() => handleSort(header.title)}
                            />
                          )}
                        </div>
                      </div>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
            </Table>
          </TableContainer>

          <TableContainer
            className="reset-table-spacing"
            sx={{ maxHeight: 314 }}
            ref={bodyRef}
            onScroll={syncScroll}
          >
            <Table
              className={`SlowShow reset-table-new ${
                mode === USER_MODE.ADMIN ? 'policy-table-admin' : 'policy-table'
              }`}
            >
              <TableBody ref={listOfItemsRef}>
                {sortedData?.map((row, index) => (
                  <React.Fragment key={`row-${index}`}>
                    <TableRow
                      className="reset-table-new__body"
                    >
                      <TableCell key={0} className="relative">
                        <div>
                          <span>
                            {formatAnyDate(
                              row?.invoices?.[0]?.invoiceDate || row?.lossDate
                            ) || t('noInfo')}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell key={1}>
                        <div>
                          <span>{row?.claimNo || t('noInfo')}</span>
                        </div>
                      </TableCell>
                      <TableCell key={2}>
                        <div>
                          <span>
                            {row?.mainDiagnosis || t('noInfo')}
                          </span>
                        </div>
                      </TableCell>
                      {mode === USER_MODE.ADMIN && (
                        <TableCell key={3}>
                          <div>
                            <span>
                              {row?.petName ||
                                t('noInfo')}
                            </span>
                          </div>
                        </TableCell>
                      )}
                      <TableCell key={4}>
                        <div>
                          <span>
                            {row?.invoices?.[0]?.veterinary ||
                              t('noInfo')}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell key={5}>
                        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
                          <span>
                            {getStatusInfo(row?.status).display}
                          </span>
                          {getStatusInfo(row?.status).hasMapping && (
                            <Tooltip title={getStatusInfo(row?.status).tooltip} placement="top">
                              <img
                                src={Question}
                                alt="info"
                                style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                              />
                            </Tooltip>
                          )}
                        </div>
                      </TableCell>
                      <TableCell key={6} sx={{ padding: '0 5px' }}>
                        <div className="d-flex flex-row justify-content-center">
                          {row?.documents && row?.documents.length > 0 ? (
                            <Tooltip title={t('seeInitDoc')} placement="top">
                              <div
                                className="back_icon_red"
                                style={{ marginRight: '10px' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setCurrentRow(row);
                                  setIsModalOpen(true);
                                }}
                              >
                                <img
                                  src={ShowFileIcon}
                                  style={{
                                    color: '#FFFAF0',
                                    top: '5px',
                                    position: 'relative',
                                  }}
                                />
                              </div>
                            </Tooltip>
                          ) : (
                            <Tooltip title={t('seeInitDoc')} placement="top">
                              <div
                                className="back_icon_red"
                                style={{ marginRight: '10px', opacity: '50%' }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <img
                                  src={ShowFileIcon}
                                  style={{
                                    color: '#FFFAF0',
                                    top: '5px',
                                    position: 'relative',
                                    opacity: '50%',
                                  }}
                                />
                              </div>
                            </Tooltip>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      )}
    </>
  );
}

export default PolicyTable;
